import ColorHash from "color-hash";

import { findConflictingLogColumn } from "@/shared/components/visualization/Panel/LogPanel/getDynamicColumns";
import {
  PlotSeriesRuleStyle,
  RuleStyle,
} from "@/shared/domain/layouts/LayoutRecord"; // Needs to be a larger concept than layout record
import { State, File, Datasource } from "@/shared/state/visualization";
import { assignSeriesColor } from "@/shared/state/visualization/assignSeriesColor";
import { cyclingColorScale } from "@/shared/state/visualization/CyclingColorScale";
import {
  isGroupablePath,
  makePlotSeries,
  validMemberIndices,
} from "@/shared/state/visualization/groupedSeries";
import {
  groupSlice,
  idForClip,
  idForMap,
  idForSeries,
} from "@/shared/state/visualization/identifiers";
import {
  dedupeMapPaths,
  makeMapPath,
} from "@/shared/state/visualization/mapPath";
import { PanelType, TopicData } from "@/shared/state/visualization/schema";
import {
  DocumentPanelData,
  ImagePanelClip,
  MapPath,
  PanelStateByType,
  PanelStates,
  PlotSeries,
  PlotSeriesMember,
  PlotSeriesMemberConfig,
  PlotSeriesMemberDefaults,
  ThreeDPanelData,
} from "@/shared/state/visualization/schema/v1";

import {
  rebinderFor,
  rebindMesh,
  rebindRotation,
  rebindVectorField,
} from "./rebindTopicData";
import { VizConfig } from "./VizConfig";

export class VizConfigBuilder {
  #state: State;

  public static transformTopicDataForPanel<T extends PanelType>(
    panelType: T,
    panelId: string,
    data: { data: TopicData[]; style: RuleStyle }[],
  ): PanelStateByType<T>["data"] {
    return panelDataTransformers[panelType](data, panelId);
  }

  constructor() {
    this.#state = VizConfig.default();
  }

  public addFile(file: File): this {
    this.#state.files.push(file);
    return this;
  }

  public addPanel(panel: PanelStates): this {
    if (this.#state.panels === undefined) {
      this.#state.panels = {
        [panel.id]: panel,
      };
      return this;
    }

    this.#state.panels[panel.id] = panel;
    return this;
  }

  public setFiles(files: File[]): this {
    this.#state.files = files;
    return this;
  }

  public setDatasources(datasources: Datasource[]): this {
    this.#state.datasources = datasources;
    return this;
  }

  public setLayout(layout: State["layout"]): this {
    this.#state.layout = layout;
    return this;
  }

  public setPanels(panels: State["panels"]): this {
    this.#state.panels = panels;
    return this;
  }

  public build(): VizConfig {
    return new VizConfig(this.#state);
  }
}

// Same settings the interactive reducer uses, so a path hydrated from a layout
// gets the colour it would have got had the user added it by hand.
const colorHash = new ColorHash({ lightness: 0.5, saturation: 0.5 });

type TopicDataWithStyle = { data: TopicData[]; style: RuleStyle };

/** A member's config from its stored style, with only the fields it stored. */
function memberConfig(style: PlotSeriesRuleStyle): PlotSeriesMemberConfig {
  return {
    ...(style.color !== undefined ? { lineColor: style.color } : {}),
    ...(style.lineWidth !== undefined ? { lineWidth: style.lineWidth } : {}),
    ...(style.pointRadius !== undefined
      ? { pointRadius: style.pointRadius }
      : {}),
    ...(style.yAxis !== undefined ? { yAxis: style.yAxis } : {}),
    ...(style.label !== undefined ? { label: style.label } : {}),
    ...(style.transform !== undefined ? { transform: style.transform } : {}),
  };
}

/**
 * What a group's rule style gives the members the group creates: every
 * per-line field it sets but color, or undefined when it sets none. A
 * rule-level color describes one line; a member takes the panel's color scale
 * per index instead.
 */
function memberStyleFromRule(
  style: RuleStyle | undefined,
): PlotSeriesMemberDefaults | undefined {
  if (style === undefined) {
    return undefined;
  }
  const { lineColor: _lineColor, ...defaults } = memberConfig(style);
  return Object.keys(defaults).length > 0 ? defaults : undefined;
}

/**
 * A group's members as its rule lists them. A layout is stored as-is and may
 * be edited by hand, so the indices are held to those the path admits (see
 * {@link validMemberIndices}): a range bounds them, but the recording's
 * declared element count does not, so a member past that count is kept; it
 * draws nothing, and the plot's notice counts it.
 */
function restoredMembers(
  members: NonNullable<RuleStyle["members"]>,
  data: TopicData,
): PlotSeriesMember[] {
  return validMemberIndices(
    members.map((member) => member.index),
    groupSlice(data) ?? { start: 0 },
  ).map((index) => {
    const style = members.find((member) => member.index === index)?.style;
    return {
      id: idForSeries(),
      index,
      visible: true,
      ...(style !== undefined ? { config: memberConfig(style) } : {}),
    };
  });
}

interface PanelDataTransformer {
  Document: (
    topicDataList: TopicDataWithStyle[],
    panelId: string,
  ) => DocumentPanelData | null;
  Log: (topicDataList: TopicDataWithStyle[], panelId: string) => TopicData[];
  Image: (
    topicDataList: TopicDataWithStyle[],
    panelId: string,
  ) => ImagePanelClip[];
  Plot: (topicDataList: TopicDataWithStyle[], panelId: string) => PlotSeries[];
  Map: (topicDataList: TopicDataWithStyle[], panelId: string) => MapPath[];
  ThreeD: (
    topicDataList: TopicDataWithStyle[],
    panelId: string,
  ) => ThreeDPanelData;
  RawMessage: (
    topicDataList: TopicDataWithStyle[],
    panelId: string,
  ) => TopicData[];
}

/**
 * Converts a structured list of {@link TopicData} into a function that can be used to populate a panel's data.
 *
 * The reason for the 2-D list is because some panel types (Map panel) expect multiple TopicData objects
 * in its data field.
 *
 */
const panelDataTransformers: PanelDataTransformer = {
  Document: () => null, // Document panel doesn't use topic data
  Log: (topicDataList) => {
    // A topic that spans multiple files gets a distinct id per file but one shared
    // name, so match on name to load all its files, not just the first match's.
    const topicData = topicDataList.flatMap((item) => item.data);
    const firstMatch = topicData[0];

    if (firstMatch === undefined) {
      return [];
    }

    // The layout path has no interactive add-gate, so apply the same conflict
    // check canAddToLogPanel enforces on drag-and-drop: merge the same-named
    // files earliest-first (a stable order, so the survivor is deterministic)
    // and drop any whose column would clash on data type or categories. Without
    // this, a saved layout could silently mislabel one file's values against
    // another's (e.g. categorical codes decoded with the wrong dictionary).
    const sameName = topicData
      .filter((data) => data.topic.name === firstMatch.topic.name)
      .sort((a, b) => {
        const aStart = BigInt(a.topic.startTime || "0");
        const bStart = BigInt(b.topic.startTime || "0");
        if (aStart < bStart) {
          return -1;
        }
        if (aStart > bStart) {
          return 1;
        }
        return 0;
      });

    const accepted: TopicData[] = [];
    for (const data of sameName) {
      if (findConflictingLogColumn(data, accepted) === undefined) {
        accepted.push(data);
      }
    }
    return accepted;
  },
  Image: (topicDataList) =>
    topicDataList
      .flatMap((item) => item.data)
      .map((data) => ({ id: idForClip(), data })),
  Plot: (topicDataList, panelId) => {
    const series: PlotSeries[] = [];
    topicDataList.forEach((item) => {
      // Add a new series for each entry in item.data but with the rule's style
      item.data.forEach((data) => {
        if (isGroupablePath(data)) {
          // A rule that names members restores them as the user's own choice
          // (see `restoredMembers`). A rule that names none, whether it has no
          // `members` key (a layout saved while the group held its default
          // members, or one the AI layout tool wrote) or an empty list, opens
          // as a just-added array does, its default members styled from the
          // rule's own style (see `makePlotSeries`).
          const storedMembers = item.style?.members ?? [];
          series.push(
            storedMembers.length === 0
              ? makePlotSeries(
                  panelId,
                  data,
                  idForSeries(),
                  memberStyleFromRule(item.style),
                )
              : {
                  kind: "group",
                  id: idForSeries(),
                  data,
                  visible: true,
                  members: restoredMembers(storedMembers, data),
                },
          );
          return;
        }
        series.push({
          id: idForSeries(),
          data,
          visible: true,
          config: {
            lineColor: item.style?.color ?? assignSeriesColor(panelId, data),
            lineWidth: item.style?.lineWidth,
            pointRadius: item.style?.pointRadius,
            yAxis: item.style?.yAxis,
            label: item.style?.label,
            transform: item.style?.transform,
          },
        });
      });
    });
    return series;
  },
  Map: (topicDataList, panelId) => {
    // One rule that matches a topic present in several files binds one path per
    // file, and every match carries that rule's style. Handing the rule's
    // colour to all of them paints the whole panel a single colour, which
    // leaves the legend unable to name any path — the panel looks like one
    // track. Only the first path takes the rule's colour; the rest get one
    // derived from their own id, so a path keeps its colour when other paths
    // are removed. This mirrors what the interactive add-path reducer does.
    const claimedRuleColors = new Set<string>();

    const colorFor = (
      ruleColor: string | undefined,
      pathId: string,
    ): string => {
      if (ruleColor === undefined) {
        return cyclingColorScale.getNextColorForPanel(panelId);
      }
      if (!claimedRuleColors.has(ruleColor)) {
        claimedRuleColors.add(ruleColor);
        return ruleColor;
      }
      return colorHash.hex(pathId);
    };

    // Collapse paths with duplicate ids so remove-by-id and React keys work.
    return dedupeMapPaths(
      topicDataList.map((item) => {
        const pathId = idForMap(item.data);
        return makeMapPath(item.data, {
          lineColor: colorFor(item.style?.color, pathId),
          label: item.style?.label,
        });
      }),
    );
  },
  ThreeD: (topicDataList) => {
    const data: ThreeDPanelData = {
      entities: {},
    };
    for (const topicData of topicDataList) {
      const config = topicData.style.threeDComponents;
      if (!config) {
        continue;
      }
      data.entities[config.entityId] ??= { id: config.entityId };
      const entity = data.entities[config.entityId];
      if (config.position) {
        entity.position = {
          transformation: config.position.transformation,
          basePreset: config.position.basePreset,
          right:
            config.position.right.type === "fixed"
              ? config.position.right.value
              : topicData.data[config.position.right.topicDataIndex],
          rightScale: config.position.right.scale,
          forward:
            config.position.forward.type === "fixed"
              ? config.position.forward.value
              : topicData.data[config.position.forward.topicDataIndex],
          forwardScale: config.position.forward.scale,
          up:
            config.position.up.type === "fixed"
              ? config.position.up.value
              : topicData.data[config.position.up.topicDataIndex],
          upScale: config.position.up.scale,
        };
      }
      // A rotation, a model's joints, a vector field and a point cloud are
      // stored with their bindings whole, so each binding is read back from
      // this match rather than from the recording the layout was saved on.
      const rebind = rebinderFor(topicData.data);
      // Guarded rather than assigned unconditionally: `lodash.isEqual` counts an
      // absent key and an `undefined` one as different, so writing
      // `rotation: undefined` onto an entity that never had one would make
      // `diffVizState` report a change the user did not make.
      if (config.rotation) {
        entity.rotation = rebindRotation(config.rotation, rebind);
      }
      if (config.mesh) {
        entity.mesh = rebindMesh(config.mesh, rebind);
      }
      if (config.path) {
        entity.path = config.path;
      }
      if (config.vectorField) {
        entity.vectorField = rebindVectorField(config.vectorField, rebind);
      }
      // The cloud reads the topic this match found, as a position's axes do,
      // so a layout opened on another recording draws that recording's cloud
      // rather than one the workspace does not hold. A rule saved before the
      // index was written still listed the cloud's topic among its
      // requirements, so its source is found by name and path instead.
      if (config.pointCloud) {
        const matchedSource =
          config.pointCloudTopicDataIndex === undefined
            ? rebind(config.pointCloud.source)
            : topicData.data[config.pointCloudTopicDataIndex];
        entity.pointCloud = matchedSource
          ? { ...config.pointCloud, source: matchedSource }
          : config.pointCloud;
      }
    }
    return data;
  },
  RawMessage: (topicDataList) => {
    // A RawMessage panel renders exactly one topic: its worker keys the time
    // index and the reader on state[0].topic.id, then reads every entry's
    // messagePath.id out of that single topic. When a layout is applied to a
    // workspace with multiple files that share a topic name, each rule matches
    // once per file (distinct topic id, distinct message-path ids), so a naive
    // flatMap would feed the first topic's reader message-path ids that belong
    // to other topics — the reader can't resolve them and the panel errors with
    // "Unable to read topic data". Keep only the first matched topic's paths.
    const topicData = topicDataList.flatMap((item) => item.data);
    const firstMatch = topicData[0];

    if (firstMatch === undefined) {
      return [];
    }

    return topicData.filter((data) => data.topic.id === firstMatch.topic.id);
  },
};
